import { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../../../hooks/redux';
import { useAppSelector } from '../../../hooks/redux';
import {
  resetAIPowered,
  isAnalyzingDescription,
  isLoadingNextQuestion,
  setEditAnswerClarificationContext,
  setIsEditingQuestionnaireItem
} from '../../../store/slices/aiPoweredSlice';
import { AIProcessingLoader } from '../../ui/AIProcessingLoader/AIProcessingLoader';
import { useAIClassification } from '../../../hooks/useAIClassification';
import { Sparkles } from '../../../icons/SvgIcons';
import { QuestionaireComponent } from '../../ui/Questionaire/QuestionaireComponent';
import { QuestionnaireReport } from '../Report/QuestionnaireReport';
import ClassificationPath from './ClassificationPath';
import { Divider } from '../../ui/Divider/Divider';

type AIPoweredQuestionnaireProps = {};

const AIPoweredQuestionnaire = (props: AIPoweredQuestionnaireProps) => {
  const dispatch = useAppDispatch();
  const startRequestInFlightRef = useRef(false);
  const { isEditClassificationPathEnabled } = useAppSelector(
    (state) => state.aiPowered
  );
  const { aiSystemId } = useAppSelector((state) => state.stepper);
  const analyzingDescription = useAppSelector(isAnalyzingDescription);
  const loadingNextQuestion = useAppSelector(isLoadingNextQuestion);

  const {
    aiPowered,
    startClassification,
    submitFollowUpAnswer,
    rerunClassification
  } = useAIClassification(aiSystemId);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const shouldShowFooterClarification = (answer: {
    answer: string;
    options?: string[];
  }) =>
    Boolean(
      answer.options?.length &&
        answer.options.some((option) => option.trim() === answer.answer.trim())
    );

  const description = aiPowered.currentJob?.description || '';
  const followUpQuestion = aiPowered.currentJob?.followUpQuestion || '';
  const followUpOptions = aiPowered.currentJob?.followUpOptions || [];

  const isAwaitingInput =
    aiPowered.currentJob?.processState === 'AWAITING_USER_INPUT';
  const isAnswerDisabled =
    !isAwaitingInput || aiPowered.submittingAnswer || aiPowered.polling;

  const aiReasoning = aiPowered.currentJob?.reasoning || '';
  const aiReasoningHeader = aiPowered.currentJob?.classificationBasis || '';
  const aiDescriptionQuality = aiPowered.currentJob?.descriptionQuality || '';
  const isJobFinalized = aiPowered.currentJob?.processState === 'FINALIZED';
  const finalReport = isJobFinalized ? aiPowered.currentJob?.finalReport : null;

  const showAiReasoning = isJobFinalized && aiReasoning;

  const showClassificationPath =
    isJobFinalized && (aiPowered.currentJob?.followUpAnswers.length ?? 0) > 0;

  const handleSubmitAnswer = async (answer: string) => {
    const trimmedAnswer = answer.trim();
    if (!trimmedAnswer || isAnswerDisabled) return;

    await submitFollowUpAnswer(trimmedAnswer);
  };

  const handleDescriptionUpdate = async (newDescription: string) => {
    const trimmedDescription = newDescription.trim();
    if (!trimmedDescription || trimmedDescription === description.trim())
      return;

    await rerunClassification({
      regulation: 'EU_AI_ACT',
      description: trimmedDescription
    });
  };

  const handleAnswerUpdate = async (index: number, newAnswer: string) => {
    const trimmedAnswer = newAnswer.trim();
    if (
      !trimmedAnswer ||
      trimmedAnswer === aiPowered.followUpAnswers[index]?.answer
    ) {
      return;
    }

    const nextFollowUpAnswers = aiPowered.followUpAnswers.map(
      (answer, answerIndex) =>
        answerIndex === index ? { ...answer, answer: trimmedAnswer } : answer
    );

    await rerunClassification({
      regulation: 'EU_AI_ACT',
      followUpAnswers: nextFollowUpAnswers,
      editedAtIndex: index
    });
  };

  const handleEditStart = (
    itemIndex: number,
    answerIndex: number | null = null,
    showFooterClarification = false
  ) => {
    setEditingIndex(itemIndex);
    dispatch(setIsEditingQuestionnaireItem(true));
    dispatch(
      setEditAnswerClarificationContext({
        index: answerIndex,
        show: showFooterClarification
      })
    );
  };

  const handleEditEnd = () => {
    setEditingIndex(null);
    dispatch(setIsEditingQuestionnaireItem(false));
    dispatch(setEditAnswerClarificationContext({ index: null, show: false }));
  };

  const isItemDisabled = (itemIndex: number) =>
    editingIndex !== null && itemIndex > editingIndex;

  useEffect(() => {
    if (!aiSystemId) return;

    if (aiPowered.aiSystemId && aiPowered.aiSystemId !== aiSystemId) {
      dispatch(resetAIPowered());
      return;
    }

    if (
      aiPowered.loading ||
      aiPowered.isInitialized ||
      startRequestInFlightRef.current
    ) {
      return;
    }

    const start = async () => {
      startRequestInFlightRef.current = true;
      await startClassification();
      startRequestInFlightRef.current = false;
    };

    start();
  }, [
    aiPowered.aiSystemId,
    aiPowered.isInitialized,
    aiPowered.loading,
    aiSystemId,
    dispatch,
    startClassification
  ]);

  useEffect(() => {
    return () => {
      dispatch(setIsEditingQuestionnaireItem(false));
      dispatch(setEditAnswerClarificationContext({ index: null, show: false }));
    };
  }, [dispatch]);

  useEffect(() => {
    if (!aiPowered.isEditingQuestionnaireItem) {
      setEditingIndex(null);
    }
  }, [aiPowered.isEditingQuestionnaireItem]);

  if (analyzingDescription) {
    return (
      <div className="relative w-full h-full">
        <div className="absolute w-full h-full">
          <AIProcessingLoader message="Analyzing Description..." />
        </div>
      </div>
    );
  }

  return (
    <div className="relative px-12 mb-4">
      {showClassificationPath ? (
        <ClassificationPath
          description={description}
          descriptionQuality={aiDescriptionQuality}
          followUpAnswers={aiPowered.followUpAnswers}
          loadingNextQuestion={loadingNextQuestion}
          rerunLoading={aiPowered.rerunLoading}
          editingIndex={editingIndex}
          isItemDisabled={isItemDisabled}
          onDescriptionUpdate={handleDescriptionUpdate}
          onAnswerUpdate={handleAnswerUpdate}
          onEditStart={handleEditStart}
          onEditEnd={handleEditEnd}
        />
      ) : (
        <>
          {/* Description */}
          <QuestionaireComponent
            icon={<Sparkles />}
            mainText={aiDescriptionQuality}
            description={description}
            descriptionTitle="AI System Information"
            isLoading={false}
            isEditable={!loadingNextQuestion}
            isGrayedOut={false}
            isDisabled={aiPowered.rerunLoading || isItemDisabled(0)}
            onDescriptionUpdate={handleDescriptionUpdate}
            onEditStart={() => handleEditStart(0)}
            onEditEnd={handleEditEnd}
          />

          {/* Answered Questions */}
          <div className="flex flex-col gap-4 mt-4">
            {aiPowered.followUpAnswers.map((answer, index) => {
              const itemIndex = index + 1;

              return (
                <div
                  key={answer.question}
                  {...(editingIndex === itemIndex
                    ? { 'data-ai-active-question': 'true' }
                    : {})}
                >
                  <QuestionaireComponent
                    icon={<Sparkles />}
                    mainText={answer.question}
                    isLoading={false}
                    isEditable={!loadingNextQuestion}
                    isGrayedOut={false}
                    isDisabled={isItemDisabled(itemIndex)}
                    isEditActive={editingIndex === itemIndex}
                    userAnswer={answer.answer}
                    answerOptions={answer.options}
                    onUserAnswerUpdate={(newAnswer) =>
                      handleAnswerUpdate(index, newAnswer)
                    }
                    onEditStart={() =>
                      handleEditStart(
                        itemIndex,
                        index,
                        shouldShowFooterClarification(answer)
                      )
                    }
                    onEditEnd={handleEditEnd}
                  />
                </div>
              );
            })}

            {/* Follow up question */}
            {followUpQuestion && (
              <div
                className="flex flex-col gap-0"
                {...(editingIndex === null
                  ? { 'data-ai-active-question': 'true' }
                  : {})}
              >
                <QuestionaireComponent
                  icon={<Sparkles />}
                  mainText={followUpQuestion}
                  isLoading={false}
                  isEditable={false}
                  isGrayedOut={false}
                  isDisabled={!isAwaitingInput || editingIndex !== null}
                  options={followUpOptions}
                  onOptionSelect={handleSubmitAnswer}
                />
              </div>
            )}

            {/* Loading Question */}
            {loadingNextQuestion && (
              <QuestionaireComponent icon={<Sparkles />} isLoading={true} />
            )}
          </div>
        </>
      )}

      {/* AI Reasoning */}
      {showAiReasoning && (
        <>
          <QuestionaireComponent
            key="ai-reasoning"
            icon={<Sparkles />}
            mainText={aiReasoningHeader}
            reasoning={aiReasoning}
            isLoading={false}
            isEditable={false}
            isGrayedOut={false}
            isDisabled={
              editingIndex !== null || isEditClassificationPathEnabled
            }
          />
          {finalReport && <Divider classes="my-9" />}
        </>
      )}

      {/* Report */}
      {finalReport && (
        <QuestionnaireReport
          finalReport={finalReport}
          classification={aiPowered.currentJob?.classification}
          legalBasis={aiPowered.currentJob?.classificationBasis}
          isEditable={!loadingNextQuestion}
          isDisabled={
            aiPowered.rerunLoading ||
            editingIndex !== null ||
            isEditClassificationPathEnabled
          }
          onDescriptionUpdate={handleDescriptionUpdate}
          onEditStart={() => handleEditStart(0)}
          onEditEnd={handleEditEnd}
        />
      )}
    </div>
  );
};

export default AIPoweredQuestionnaire;
